import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";

// 2026-09-23 · Identidad del comensal desde el QR cliente.
//
// El móvil genera un UUID opaco al escanear (o lo recupera de
// localStorage si vuelve a entrar). El backend lo asocia a un
// numero de comensal en la ComensalesJson de la sesión de mesa
// — mismo modelo que usa el TPV camarero, sin duplicidad.
//
// Persistencia: la clave `tmoQr:comensal:{idSession}` guarda
// { deviceToken, numero } — al recargar la app el hook lee de
// aquí y llama /auto-register para reconfirmar (idempotente).

const STORAGE_PREFIX = "tmoQr:comensal:";

const buildKey = (idSession) => `${STORAGE_PREFIX}${idSession}`;

const readFromStorage = (idSession) => {
  if (!idSession) return null;
  try {
    const raw = window.localStorage.getItem(buildKey(idSession));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const persistToStorage = (idSession, data) => {
  if (!idSession) return;
  try {
    if (data) {
      window.localStorage.setItem(buildKey(idSession), JSON.stringify(data));
    } else {
      window.localStorage.removeItem(buildKey(idSession));
    }
  } catch {}
};

const generateDeviceToken = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, "");
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`;
};

const initialState = {
  // Numero + token del comensal DEL DEVICE actual — persistido en
  // localStorage bajo la clave `tmoQr:comensal:{idSession}`.
  myComensal: null, // { numero, deviceToken, nombre }
  // Lista de todos los comensales visibles en la mesa (para pintar
  // "Mi cuenta / Toda la mesa" y mover productos a otro comensal).
  comensales: [], // [{ numero, nombre }]
  isRegistering: false,
  isLoadingComensales: false,
  // Estado del split coordinado — null si no hay uno abierto.
  // { dinerCount, amountPerDiner, totalAmount, initiatedByDeviceToken,
  //   createdAt, closedAt, slots: [{ index, paidByDeviceToken?,
  //   paidByNumero?, paidAt? }] }
  split: null,
  error: null,
};

const slice = createSlice({
  name: "dinerQr",
  initialState,
  reducers: {
    startRegistering(state) {
      state.isRegistering = true;
      state.error = null;
    },
    registerSuccess(state, action) {
      state.isRegistering = false;
      state.myComensal = action.payload;
    },
    registerError(state, action) {
      state.isRegistering = false;
      state.error = action.payload || "auto-register error";
    },
    comensalesLoading(state) {
      state.isLoadingComensales = true;
    },
    comensalesLoaded(state, action) {
      state.isLoadingComensales = false;
      state.comensales = action.payload || [];
    },
    splitLoaded(state, action) {
      state.split = action.payload || null;
    },
    reset(state) {
      state.myComensal = null;
      state.comensales = [];
      state.split = null;
      state.error = null;
    },
  },
});

export default slice.reducer;

const {
  startRegistering,
  registerSuccess,
  registerError,
  comensalesLoading,
  comensalesLoaded,
  splitLoaded,
  reset,
} = slice.actions;

// Exportamos `reset` como `resetDinerQr` para llamarlo desde logout / cambio de mesa.
export { reset as resetDinerQr };

// ------------------------------ Thunks ------------------------------

/**
 * Auto-registra el device del cliente QR como comensal en la sesión.
 * Idempotente: si el device ya está registrado (deviceToken existe en
 * el ComensalesJson) el backend devuelve el mismo numero.
 *
 * Cuando el device NO tenía token guardado, se genera uno cliente-side
 * y se envía. El backend lo persiste en el JSON para el próximo
 * auto-register.
 */
export function autoRegisterComensalQr(idSession) {
  return async (dispatch) => {
    if (!idSession) return null;
    dispatch(startRegistering());

    let stored = readFromStorage(idSession);
    // Si nunca antes → generamos token cliente-side. Si ya existe,
    // reusamos el token para reconfirmar el mismo numero.
    const deviceToken = stored?.deviceToken || generateDeviceToken();

    try {
      const res = await axios.post(
        `dinerSplit/session/${idSession}/auto-register`,
        { deviceToken },
      );
      // 2026-09-23 · Se incluye idSession en el payload para poder
      // detectar cross-session leaks cuando redux-persist rehidrata
      // myComensal después de cambiar de mesa (nueva sesión).
      const payload = {
        idSession,
        numero: res.data.numero,
        deviceToken: res.data.deviceToken,
        nombre: res.data.nombre || null,
      };
      persistToStorage(idSession, payload);
      dispatch(registerSuccess(payload));
      return payload;
    } catch (e) {
      dispatch(registerError(e?.response?.data?.message || e?.message));
      return null;
    }
  };
}

/**
 * Carga la lista de comensales visibles en la mesa — para pintar la
 * vista "Mi cuenta / Toda la mesa" y el sheet "Mover a otro comensal".
 * Endpoint público (strip de device tokens de otros comensales).
 */
export function fetchComensalesQr(idSession) {
  return async (dispatch) => {
    if (!idSession) return [];
    dispatch(comensalesLoading());
    try {
      const res = await axios.get(
        `dinerSplit/session/${idSession}/comensales-public`,
      );
      dispatch(comensalesLoaded(res.data || []));
      return res.data;
    } catch (e) {
      dispatch(comensalesLoaded([]));
      return [];
    }
  };
}

/**
 * Mover MIS productos a otro comensal desde el QR. `entries` es un
 * array de {pedidoID, productoID, complementoID, counterProductID,
 * lineIndex, sourceComensal, newComensal, unidadesToMove?}. El
 * backend descarta cualquier entry cuyo sourceComensal no coincida
 * con el numero asociado al deviceToken (solo el propietario mueve).
 */
export function moverMisProductosQr(idSession, entries) {
  return async (_dispatch, getState) => {
    if (!idSession || !entries || entries.length === 0) return null;
    const state = getState().dinerQr;
    const deviceToken = state?.myComensal?.deviceToken;
    if (!deviceToken) return null;
    try {
      const res = await axios.post(
        `dinerSplit/session/${idSession}/mis-productos/mover`,
        { deviceToken, entries },
      );
      return res.data;
    } catch (e) {
      throw e;
    }
  };
}

/**
 * Refresca la sesión QR (session.pedidos + cuenta) tras una mutación.
 * Reutiliza el endpoint autenticado `data/session/{mesa}/{restaurante}`
 * — el cliente QR ya tiene JWT desde StartSessionQr. Dispatch a
 * `receiveDataSuccess` de `authJwt` que reemplaza `state.session`.
 */
export function refreshQrSession(idMesa, idRestaurante) {
  return async (dispatch) => {
    if (!idMesa || !idRestaurante) return null;
    try {
      const res = await axios.get(`data/session/${idMesa}/${idRestaurante}`);
      if (res?.data?.session) {
        // receiveDataSuccess reemplaza state.authJwt.session completo.
        dispatch({
          type: "authJwt/receiveDataSuccess",
          payload: res.data.session,
        });
        return res.data.session;
      }
    } catch (e) {
      // Fail-open: si el endpoint no responde (sesion cerrada, red…)
      // no propagamos — la UI solo pierde el refresh visual.
    }
    return null;
  };
}

/**
 * Inicia el flujo Stripe "pagar mi cuenta" — envía las entries
 * (líneas del comensal a pagar) y recibe la URL de Stripe checkout.
 * El caller redirige window.location a esa URL. El backend valida
 * que las líneas pertenecen al comensal identificado por deviceToken.
 */
export function payMyLinesQr(idSession, entries) {
  return async (_dispatch, getState) => {
    if (!idSession || !entries || entries.length === 0) return null;
    const state = getState().dinerQr;
    const deviceToken = state?.myComensal?.deviceToken;
    // 2026-09-26 · Antes hacía `return null` silencioso → el caller
    // solo veía "No se pudo iniciar el pago" sin pista. Ahora lanza
    // error explícito con el motivo para que el snackbar informe.
    if (!deviceToken) {
      throw new Error(
        "No estás identificado como comensal. Recarga la página o cierra la sesión y vuelve a escanear el QR.",
      );
    }
    const res = await axios.post(
      `dinerSplit/session/${idSession}/checkout-partial`,
      { deviceToken, entries },
    );
    return res.data; // { url, amount, lineCount }
  };
}

/**
 * Pago de líneas seleccionadas (cualquier línea de la mesa,
 * no solo las propias). Endpoint distinto que no filtra por
 * comensal — el cliente puede pagar líneas de otros ("pago por
 * mi amigo"). Auditoría persiste el deviceToken igualmente.
 */
export function payAnyLinesQr(idSession, entries) {
  return async (_dispatch, getState) => {
    if (!idSession || !entries || entries.length === 0) return null;
    const state = getState().dinerQr;
    const deviceToken = state?.myComensal?.deviceToken;
    if (!deviceToken) return null;
    const res = await axios.post(
      `dinerSplit/session/${idSession}/checkout-lines`,
      { deviceToken, entries },
    );
    return res.data;
  };
}

/**
 * "Salir" — libera las líneas sin pagar del comensal y limpia el
 * device token del móvil. El resto de comensales pueden reasignarlas.
 */
export function leaveComensalQr(idSession) {
  return async (dispatch, getState) => {
    if (!idSession) return;
    const state = getState().dinerQr;
    const deviceToken = state?.myComensal?.deviceToken;
    if (!deviceToken) {
      persistToStorage(idSession, null);
      dispatch(reset());
      return;
    }
    try {
      await axios.post(`dinerSplit/session/${idSession}/leave`, {
        deviceToken,
      });
    } catch {
      // Aunque falle el backend, limpiamos local — el usuario ya
      // decidió salir. Camarero puede reasignar desde el TPV.
    }
    persistToStorage(idSession, null);
    dispatch(reset());
  };
}

// -------------------- Thunks Split coordinado (Fase D) --------------------

/** Estado actual del split — null si no hay uno abierto. */
export function fetchSplitStateQr(idSession) {
  return async (dispatch) => {
    if (!idSession) return null;
    try {
      const res = await axios.get(`dinerSplit/session/${idSession}/split`);
      dispatch(splitLoaded(res.data));
      return res.data;
    } catch {
      dispatch(splitLoaded(null));
      return null;
    }
  };
}

/** Inicia un split entre N. Devuelve el estado. */
export function startSplitQr(idSession, dinerCount) {
  return async (dispatch, getState) => {
    if (!idSession) return null;
    const state = getState().dinerQr;
    const deviceToken = state?.myComensal?.deviceToken;
    if (!deviceToken) return null;
    const res = await axios.post(
      `dinerSplit/session/${idSession}/split/start`,
      { deviceToken, dinerCount },
    );
    dispatch(splitLoaded(res.data));
    return res.data;
  };
}

/** Cancela el split. Solo funciona si nadie pagó y lo inició este device. */
export function cancelSplitQr(idSession) {
  return async (dispatch, getState) => {
    if (!idSession) return;
    const state = getState().dinerQr;
    const deviceToken = state?.myComensal?.deviceToken;
    if (!deviceToken) return;
    await axios.post(`dinerSplit/session/${idSession}/split/cancel`, {
      deviceToken,
    });
    dispatch(splitLoaded(null));
  };
}

/**
 * "Pagar mi parte" — reserva slot + devuelve URL Stripe. El caller
 * hace window.location.href a la URL.
 */
export function paySplitSlotQr(idSession) {
  return async (_dispatch, getState) => {
    if (!idSession) return null;
    const state = getState().dinerQr;
    const deviceToken = state?.myComensal?.deviceToken;
    if (!deviceToken) return null;
    const res = await axios.post(
      `dinerSplit/session/${idSession}/split/pay-my-slot`,
      { deviceToken },
    );
    return res.data; // { url, slotIndex, amount }
  };
}
